import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { actions, selectors, types } from '@elab/core/store/store-locator';
import { actions as uiActions, selectors as uiSelectors } from '@elab/core/store/ui';
import { getPointCoordinates, loadAsset } from '@elab/core/utils/helpers';
import constants from '@elab/core/utils/constants';

/**
 * Component shows stores on the google map. Component just read the redux store, doesn't change it.
 * Component uses clusters to combine few stores to one bunch in case of small zoom level (Could be disabled).
 * If stores are presented - center of map and zoom changes automatically to fit stores bound.
 * In case if store list in redux store is empty - zoom and map center changes to default.
 *
 * @attr {String} [cluster-image-path] - path of cluster image used in google maps
 * @attr {String} [marker-image-path] - path of marker (store) image used in google maps
 * @attr {Number} [default-latitude = 0] - default latitude value to center map if store list is empty
 * @attr {Number} [default-longitude = 0] - default longitude value to center map if store list is empty
 * @attr {Number} [default-zoom = 15] - default zoom value of map if store list is empty
 * @attr {Number} [single-shop-zoom = 15] - zoom for single store (if some store is selected in redux, not for click on marker)
 * @attr {Number} [cluster-size = 53] - cluster area size
 * @attr {String} [cluster-text-color = 'white'] - color of text on cluster image (count of markers in the cluster)
 *
 * @attr {String} google-api-key - color of text on cluster image (count of markers in the cluster)
 * @attr {String} [google-client-id] - color of text on cluster image (count of markers in the cluster)
 * @attr {String} [google-api-version] - color of text on cluster image (count of markers in the cluster)
 * @attr {Boolean} [postponedInit] - postpone google api loading
 * @attr {Boolean} [disable-clusters] - disable points clustering functionality
 * @attr {Boolean} [has-name-as-link] - shows name as link
 * @attr {Boolean} [has-reset-default-style] - reset default style of info window
 * @attr {Object} [map-styles] - config for styling google maps
 * @attr {Number} [info-window-offset-y] - amount of pixels to move map center by y-axis when info window is showed
 * @attr {Boolean} [show-opening-hours] - show table with opening hours information
 * @attr {Array} [info-window-render-as-button] - list of elements names which should be rendered as a button
 * @attr {Boolean} [phone-with-link] - make phone field callable
 * @attr {String} [info-window-button] - Text for action button inside StoreInfoWindow
 *
 * @prop {Boolean} isMapTabOpened - become true after the tab with the map inside is opened
 * @prop {Array} stores - array of stores from store-locator
 * @prop {Object} selectedStore - selected store object (which store popup is opened)
 * @prop {String} storeSelectionId - store selection identifier (used to update position if user selects the same store twice)
 * @prop {Boolean} isGoogleMapsApiLoaded - shows if Google maps api is loaded
 *
 * @prop {String} moreInfoLabel - show link to Single Store page
 *
 * <e2-map
 *  cluster-image-path="markers-cluster.png"
 *  marker-image-path="marker.png"
 *  default-latitude="0"
 *  default-longitude="0"
 *  default-zoom="11"
 *  single-shop-zoom="20"
 *  cluster-size="53"
 *  cluster-text-color="black"
 *  google-api-version="api-key"
 *  google-api-key="3.35"
 *  google-client-id=""
 *  more-info-label
 *  show-opening-hours
 *  info-window-offset-y="100"
 *  info-window-render-as-button="["phone", "email"]"
 *>
 *  <div class="map"></div>
 *  <p v-slot="info-window-button">Click</p>
 *  <p v-slot="info-window-closed">Closed</p>
 * </e2-map>
 */

class GoogleMap extends SimpleLitElement {
    static get properties() {
        return {
            clusterImagePath: { type: String, attribute: 'cluster-image-path' },
            markerImagePath: { type: String, attribute: 'marker-image-path' },

            defaultLatitude: { type: Number, attribute: 'default-latitude' },
            defaultLongitude: { type: Number, attribute: 'default-longitude' },

            defaultZoom: { type: Number, attribute: 'default-zoom' },
            singleShopZoom: { type: Number, attribute: 'single-shop-zoom' },

            clusterSize: { type: Number, attribute: 'cluster-size' },
            clusterHeight: { type: Number, attribute: 'cluster-height' },
            clusterTextSize: { type: Number, attribute: 'cluster-text-size' },
            clusterBackgroundPosition: { type: String, attribute: 'cluster-background-position' },
            clusterTextColor: { type: String, attribute: 'cluster-text-color' },

            googleApiKey: { type: String, attribute: 'google-api-key' },
            googleClientId: { type: String, attribute: 'google-client-id' },
            googleApiVersion: { type: String, attribute: 'google-api-version' },

            postponedInit: { type: Boolean },
            disableClusters: { type: Boolean, attribute: 'disable-clusters' },
            type: { type: String, attribute: 'type' },

            isMapTabOpened: { type: Boolean, attribute: false },
            stores: { type: Array, attribute: false },
            selectedStore: { type: Object, attribute: false },
            storeSelectionId: { type: String, attribute: false },
            isGoogleMapsApiLoaded: { type: Boolean, attribute: false },
            centerPoint: { type: Object, attribute: false },
            formattedCityWithCountry: { type: Boolean, attribute: 'formatted-city-with-country' },
            moreInfoLabel: { type: String, attribute: 'more-info-label' },
            hasNameAsLink: { type: Boolean, attribute: 'has-name-as-link' },
            hasResetDefaultStyle: {
                type: Boolean,
                attribute: 'has-reset-default-style',
            },
            closedLabel: {
                type: String,
                attribute: 'closed-label',
            },
            mapStyles: { type: Array, attribute: 'map-styles' },
            infoWindowOffsetY: {
                type: Number,
                attribute: 'info-window-offset-y',
            },
            showOpeningHours: {
                type: Boolean,
                attribute: 'show-opening-hours',
            },
            infoWindowRenderAsButton: {
                type: Array,
                attribute: 'info-window-render-as-button',
            },
            phoneWithLink: { type: Boolean, attribute: 'phone-with-link' },
            centerOnSearch: { type: Boolean, attribute: 'center-on-search' },
            infoWindowButtonText: {
                type: String,
                attribute: 'info-window-button-text',
            },
            noCountdown: {
                type: Boolean,
                attribute: 'no-countdown',
            },
            noContactDetails: {
                type: Boolean,
                attribute: 'no-contact-details',
            },
            infoWindowCountdownTitleOpened: {
                type: String,
                attribute: 'info-window-countdown-title-opened',
            },
            infoWindowCountdownTitleClosed: {
                type: String,
                attribute: 'info-window-countdown-title-closed',
            },
            infoWindowHoursTitle: {
                type: String,
                attribute: 'info-window-hours-title',
            },
            infoWindowIsOutside: {
                type: Boolean,
                attribute: 'info-window-is-outside',
            },
        };
    }

    constructor() {
        super();
        this.clusterImagePath = '';
        this.markerImagePath = '';
        this.defaultLatitude = 0;
        this.defaultLongitude = 0;
        this.defaultZoom = 8;
        this.singleShopZoom = 15;
        this.clusterSize = 53;
        this.clusterHeight = 0;
        this.clusterTextSize = 0;
        this.clusterBackgroundPosition = '';
        this.clusterTextColor = 'white';
        this.googleApiVersion = '';
        this.googleApiKey = '';
        this.googleClientId = '';
        this.postponedInit = false;
        this.stores = [];

        this.__markers = [];
        this.__markersData = [];

        this.moreInfoLabel = '';
        this.hasNameAsLink = false;
        this.hasResetDefaultStyle = false;
        this.mapStyles = [];
        this.infoWindowOffsetY = 0;
        this.showOpeningHours = false;
        this.infoWindowRenderAsButton = [];
        this.centerPoint = {};
        this.centerOnSearch = false;
        this.infoWindowButtonText = '';
        this.isMapTabOpened = false;
        this.infoWindowHoursTitle = '';
        this.infoWindowIsOutside = false;
    }

    get clusterOptions() {
        return !this.clusterHeight
            ? {
                  styles: [
                      {
                          url: this.clusterImagePath,
                          width: this.clusterSize,
                          height: this.clusterSize,
                          textColor: this.clusterTextColor,
                      },
                  ],
              }
            : {
                  styles: [
                      {
                          url: this.clusterImagePath,
                          width: this.clusterSize,
                          height: this.clusterHeight,
                          textColor: this.clusterTextColor,
                          textSize: this.clusterTextSize,
                          backgroundPosition: this.clusterBackgroundPosition,
                      },
                  ],
              };
    }

    firstUpdated() {
        if (this.isGoogleMapsApiLoaded) {
            this.doInit();
        } else {
            this.postponedInit = true;
            this.dispatchEvent(
                new CustomEvent('load-google-maps-api', {
                    detail: {
                        version: this.googleApiVersion,
                        key: this.googleApiKey,
                        clientId: this.googleClientId,
                    },
                }),
            );
        }
    }

    getInfoWindowHtml(index) {
        return `
            <e2-store-info-window
                ${this.hasNameAsLink ? 'name-as-link' : ''}
                ${this.hasResetDefaultStyle ? 'reset-default-style' : ''}
                more-info="${this.moreInfoLabel}"
                index="${index}"
                ${this.formattedCityWithCountry ? 'formatted-city-with-country' : ''}
                ${this.showOpeningHours ? 'show-opening-hours' : ''}
                ${this.phoneWithLink ? 'phone-with-link' : ''}
                render-as-button='${JSON.stringify(this.infoWindowRenderAsButton)}'
                ${
                    this.type === constants.postLocatorName
                        ? `
                    action-type="${types.APPLY_DELIVERY_POST_POINT}"
                `
                        : ''
                }
                action-button-text="${this.infoWindowButtonText}"
                ${this.noCountdown ? 'no-countdown' : ''}
                ${this.noContactDetails ? 'no-contact-details' : ''}
                closed-label="${this.closedLabel}"
                ${
                    this.infoWindowHoursTitle !== ''
                        ? `info-window-hours-title="${this.infoWindowHoursTitle}"`
                        : ''
                }
                info-window-countdown-title-opened="${this.infoWindowCountdownTitleOpened}"
                info-window-countdown-title-closed="${this.infoWindowCountdownTitleClosed}"

            ></e2-store-info-window>
        `;
    }

    doInit() {
        const tag = this.querySelector('.map');

        if (tag) {
            this.createMap(tag);
            this.requestUpdate('stores');
            this.postponedInit = false;
        }
    }

    updated(changedProperties) {
        if (changedProperties.has('stores') && this.__map) {
            if (this.selectedStore) {
                this.dispatchEvent(new CustomEvent('select-store', { detail: false }));
            }
            this.updateMarkers();
        } else if (changedProperties.has('storeSelectionId')) {
            this.focusToSelected();
        } else if (changedProperties.has('isGoogleMapsApiLoaded')) {
            if (this.postponedInit && !!this.isGoogleMapsApiLoaded) {
                this.doInit();
            }
        } else if (changedProperties.has('isMapTabOpened') && this.isMapTabOpened) {
            this.fitBounds();
        }
    }

    createMap(tag) {
        this.__map = new window.google.maps.Map(tag, {
            center: {
                lat: this.defaultLatitude || 0,
                lng: this.defaultLongitude || 0,
            },
            zoom: this.defaultZoom,
            mapTypeControlOptions: {
                style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
            },
            clickableIcons: false,
            styles: this.mapStyles,
        });
    }

    removeMarkers() {
        if (this.disableClusters) {
            this.removeMarkersFromMap();
        } else {
            this.__markerClusterer && this.__markerClusterer.clearMarkers();
        }
        this.__markers = [];
        this.__markersData = [];
        this.__bounds = new window.google.maps.LatLngBounds();
    }

    updateMarkers() {
        this.removeMarkers();
        this.stores.forEach(this.createMarkerData);
        this.addMarkersListeners();

        if (this.disableClusters) {
            this.setMarkersOnMap();
        } else {
            this.createMarkerCluster();
        }
        if (this.centerOnSearch && this.centerPoint.latitude && this.centerPoint.longitude) {
            this.__map.setZoom(this.defaultZoom);
            this.__map.panTo({
                lat: this.centerPoint.latitude,
                lng: this.centerPoint.longitude,
            });
        } else {
            this.fitBounds();
        }
    }

    /**
     * change zoom and map center to store list bound if store list is not empty,
     * in another case - zoom and map center used from default settings
     */
    fitBounds() {
        if (!this.__map) return;

        if (this.__markersData && this.__markersData.length) {
            setTimeout(() => {
                this.__map.fitBounds(this.__bounds);
                if (this.__markersData.length === 1) {
                    this.setSingleShopZoom();
                }
            }, 0);
        } else {
            this.__map.panTo({
                lat: this.defaultLatitude || 0,
                lng: this.defaultLongitude || 0,
            });

            if (this.__map.getZoom() !== this.defaultZoom) {
                this.__map.setZoom(this.defaultZoom);
            }
        }
    }

    markerClick(id) {
        this.dispatchEvent(new CustomEvent('select-store', { detail: { storeId: id } }));
    }

    addMarkersListeners() {
        this.__markersData.forEach(({ marker, id }) => {
            marker.addListener('click', () => this.markerClick(id));
        });
    }

    setMarkersOnMap() {
        this.__markersData.forEach(({ marker }) => marker.setMap(this.__map));
        this.fitBounds();
    }

    removeMarkersFromMap() {
        this.__markersData.forEach(({ marker }) => marker.setMap(null));
    }

    /**
     * Move to selected store (selected in redux)
     */
    focusToSelected() {
        const { storeId } = this.selectedStore || {};
        const selectedMarkerData =
            storeId &&
            this.__map &&
            this.__markersData &&
            this.__markersData.find(data => data.id === storeId);

        if (!selectedMarkerData) return;

        this.__map.panTo(selectedMarkerData.coordinates);

        this.setSingleShopZoom();

        if (this.infoWindowOffsetY) {
            this.__map.panBy(0, this.infoWindowOffsetY);
        }

        this.openInfoWindow(selectedMarkerData.id);
    }

    createMarkerData = (store, index) => {
        const coordinates = getPointCoordinates(store);
        if (!coordinates) return;

        const id = store.name; // name is a unique id of the store
        let infoWindow = null;

        if (!this.infoWindowIsOutside) {
            const infoWindowContent = this.getInfoWindowHtml(index);

            if (infoWindowContent) {
                infoWindow = new window.google.maps.InfoWindow();
                infoWindow.setContent(infoWindowContent);
            }
        }

        const markerData = {
            id,
            marker: this.createMarker(coordinates),
            coordinates,
            infoWindow,
        };

        this.__markersData.push(markerData);
        this.__markers.push(markerData.marker);
        this.__bounds.extend(coordinates);
    };

    createMarker({ lat, lng }) {
        return new window.google.maps.Marker({
            icon: this.markerImagePath,
            position: new window.google.maps.LatLng(lat, lng),
        });
    }

    async createMarkerCluster() {
        await loadAsset('MarkerClusterer');
        const { MarkerClusterer } = window.e2_assets;
        this.__markerClusterer = new MarkerClusterer(
            this.__map,
            this.__markers,
            this.clusterOptions,
        );
    }

    setSingleShopZoom() {
        this.singleShopZoom
            ? this.__map.setZoom(this.singleShopZoom)
            : this.__map.setZoom(this.defaultZoom);
    }

    openInfoWindow(id) {
        const point = this.__markersData.find(data => data.id === id) || {};
        if (!point.infoWindow) return;

        if (this.__lastInfoWindow) {
            this.__lastInfoWindow.close();
        }

        point.infoWindow.open(this.__map, point.marker);
        this.__lastInfoWindow = point.infoWindow;
        window.google.maps.event.addListener(point.infoWindow, 'closeclick', () => {
            this.dispatchEvent(
                new CustomEvent('set-custom-handle-close', {
                    detail: true,
                }),
            );
        });
    }
}

const mapStateToProps = state => {
    const data = selectors.getData(state);
    const sourcePoint = {
        latitude: data.sourceLatitude,
        longitude: data.sourceLongitude,
    };
    return {
        stores: selectors.getStores(state),
        selectedStore: selectors.getSelectedStore(state),
        storeSelectionId: selectors.getStoreSelectionId(state),
        isGoogleMapsApiLoaded: selectors.isGoogleMapsApiLoaded(state),
        isMapTabOpened: uiSelectors.getTabName(state) === 'map-view',
        centerPoint: selectors.isLocationTextPresent(state) ? sourcePoint : {},
    };
};

const mapDispatchToEvents = dispatch => ({
    'load-google-maps-api': ({ detail }) => dispatch(actions.loadGoogleMapsApi(detail)),
    'select-store': ({ detail }) => dispatch(actions.selectStore(detail)),
    'set-custom-handle-close': ({ detail }) => {
        dispatch(uiActions.setCustomHandleClose(detail));
    },
});

class DynamicGoogleMap extends SimpleLitElement {
    static get properties() {
        return {
            markerImagePath: { type: String, attribute: 'marker-image-path' },
            googleApiKey: { type: String, attribute: 'google-api-key' },
            googleClientId: { type: String, attribute: 'google-client-id' },
            googleApiVersion: { type: String, attribute: 'google-api-version' },
            defaultZoom: { type: Number, attribute: 'default-zoom' },
            point: { type: Object },
            isGoogleMapsApiLoaded: { type: Boolean, attribute: false },
            mapStyles: { type: Array, attribute: 'map-styles' },
            dynamic: { type: Boolean },
            customLabel: { type: String, attribute: 'custom-label' },
            markerColor: { type: String, attribute: 'marker-color' },
        };
    }

    constructor() {
        super();
        this.defaultZoom = 11;
        this.googleApiVersion = '';
        this.googleApiKey = '';
        this.googleClientId = '';
        this.point = null;
        this.mapStyles = [];
        this.customLabel = '';
        this.markerColor = '0xFF0000';
    }

    createStyleString() {
        let results = [];
        this.mapStyles.forEach(s =>
            results.push(
                `style=${encodeURIComponent(
                    `feature:${s.featureType}|color:${s.stylers[0].color.replace('#', '0x')}`,
                )}`,
            ),
        );
        return results.join('&');
    }

    createStaticMapUrl() {
        const markerLabel = this.customLabel ? `label:${this.customLabel}|` : '';
        const markers = encodeURIComponent(
            `color:0x${this.markerColor}|${markerLabel}${this.point.latitude},${this.point.longitude}`,
        );
        const styles = this.createStyleString();
        const { width, height } = this.parentNode.getBoundingClientRect();
        const size = `size=${parseInt(width)}x${parseInt(height)}`;
        const apiKey = `key=${this.googleApiKey}`;

        return `https://maps.googleapis.com/maps/api/staticmap?${size}&markers=${markers}&${styles}&${apiKey}`;
    }

    doInit() {
        const tag = this.querySelector('.dynamic-map');

        if (tag) {
            this.createMap(tag);
            this.postponedInit = false;
        }
    }

    createMap(tag) {
        const position = new window.google.maps.LatLng(this.point.latitude, this.point.longitude);
        this.__map = new window.google.maps.Map(tag, {
            zoom: this.defaultZoom,
            center: position,
            mapTypeControlOptions: {
                style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
            },
            clickableIcons: false,
            styles: this.mapStyles,
        });

        this.__marker = new window.google.maps.Marker({
            position,
            icon: this.markerImagePath,
        });
        this.__marker.setMap(this.__map);
    }

    firstUpdated() {
        if (this.dynamic) {
            if (this.isGoogleMapsApiLoaded) {
                this.doInit();
            } else {
                this.postponedInit = true;
                this.dispatchEvent(
                    new CustomEvent('load-google-maps-api', {
                        detail: {
                            version: this.googleApiVersion,
                            key: this.googleApiKey,
                            clientId: this.googleClientId,
                        },
                    }),
                );
            }
        }
    }

    updated(changedProperties) {
        if (this.dynamic && changedProperties.has('isGoogleMapsApiLoaded')) {
            if (this.postponedInit && !!this.isGoogleMapsApiLoaded) {
                this.doInit();
            }
        }
    }

    renderDynamic() {
        return html`
            <div class="dynamic-map" style="width: 100%; height: 100%;"></div>
        `;
    }

    renderStatic() {
        return html`
            <img src="${this.createStaticMapUrl()}" />
        `;
    }

    render() {
        return this.dynamic ? this.renderDynamic() : this.renderStatic();
    }
}

const mapStateToPropsDynamic = state => ({
    isGoogleMapsApiLoaded: selectors.isGoogleMapsApiLoaded(state),
});

export default () => {
    customElements.define('e2-map', connect(mapStateToProps, mapDispatchToEvents)(GoogleMap));
    customElements.define(
        'e2-dynamic-map',
        connect(mapStateToPropsDynamic, mapDispatchToEvents)(DynamicGoogleMap),
    );
};
